'use client'

import { ImageWithFallback as Image } from '@/components/common/image-with-fallback'
import { generateTagTrackingValue } from '@/components/common/product-card/helper'
import { ProductAddMobile } from '@/components/common/product-card/product-add-mobile'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { stringToSlug } from '@/lib/utils/common'
import Link from 'next/link'
import { useProductCardContext } from './context/product-card-context'
import { ProductItemHeader } from './product-item-header'
import { ProductItemImages } from './product-item-images'
import { ProductItemSizes } from './product-item-sizes'

export const ProductItemThumbnail = ({
  gaTrackingValue,
  gaTrackingLabel,
  disableNavigation,
  hideSizes,
}: {
  gaTrackingValue?: string
  gaTrackingLabel?: string
  disableNavigation?: boolean
  hideSizes?: boolean
}) => {
  const { product, trackingClickProduct, selectedColor } =
    useProductCardContext()

  // Auto-generate tracking value from product tags if not provided
  const finalTrackingValue = product?.is_setdo
    ? 'product__listing_set-do'
    : gaTrackingValue || generateTagTrackingValue(product.tags)

  const handleClick = () => {
    if (!disableNavigation) {
      trackingClickProduct()
    }
  }

  const isFifaProduct = product?.collections?.includes(
    '69b22f304a3f281d3a477e93'
  )

  const isOutOfStock =
    typeof product?.in_stock === 'number' && product.in_stock <= 0
  const overlayLabel = product?.coming_soon
    ? 'Coming Soon'
    : isOutOfStock
      ? 'Coming Soon'
      : null

  const content = (
    <>
      <ProductItemImages />

      {overlayLabel ? (
        <div className='absolute inset-0 z-[2] flex items-center justify-center rounded-lg bg-black/40'>
          <span className='font-criteria text-base font-semibold uppercase text-light md:text-lg'>
            {overlayLabel}
          </span>
        </div>
      ) : null}

      {isFifaProduct ? (
        <div className='absolute left-2 top-2 z-[2] w-4 md:w-6'>
          <Image
            src={ImageHelper.getN7ImageUrl(
              '/image/2026/03/19/logo_fifa_official_1.png'
            )}
            alt='fifa-logo'
            width={80}
            height={120}
            className='h-auto w-full object-contain'
            loading='lazy'
            quality={75}
          />
        </div>
      ) : null}

      {!product?.icon_thumbnail?.hide && product?.icon_thumbnail?.image && (
        <div className='absolute bottom-0 left-0 z-[1] h-auto w-full rounded-b-lg'>
          <Image
            src={ImageHelper.getImageUrl(
              product?.icon_thumbnail?.image,
              '585_663'
            )}
            width={433}
            height={62}
            alt={`${product.title} thumbnail image`}
            className='block h-auto w-full rounded-b-lg object-cover'
            loading='lazy'
          />
        </div>
      )}
      <ProductItemHeader />
    </>
  )

  return (
    <div className='group relative'>
      {disableNavigation ? (
        <div className='block h-full w-full'>{content}</div>
      ) : (
        <Link
          href={
            selectedColor
              ? `${product.href}?color=${stringToSlug(selectedColor)}`
              : product.href
          }
          className='block h-full w-full'
          {...(finalTrackingValue
            ? {
                'ga-tracking-value': finalTrackingValue,
                ...(gaTrackingLabel && {
                  'ga-tracking-label': gaTrackingLabel,
                }),
              }
            : {})}
          onClick={handleClick}
          aria-label={`View details for ${product.title}`}
        >
          {content}
        </Link>
      )}
      {!hideSizes && !overlayLabel && (
        <div className='max-lg:hidden'>
          <ProductItemSizes />
        </div>
      )}
      {!overlayLabel && (
        <ProductAddMobile
          className={
            !product?.icon_thumbnail?.hide && product?.icon_thumbnail?.image
              ? 'bottom-12'
              : 'bottom-6'
          }
        />
      )}
    </div>
  )
}
